// SSE.js — DROP-IN (seed from /api/events/head, persist last id, handle hits:changed)
import { API } from "./data";
import { SSE_LAST_ID_KEY } from "./Auth";

let es = null;
let connected = false;

let reopenTimer = null;
let debTimer = null;
let idleTicker = null;

let lastActivityAt = 0;
let burstCount = 0;

const BACKOFFS = [1000, 2000, 4000, 8000, 15000];
let backoffIdx = 0;

let started = false;
let disconnectHandlerBound = false;


function dispatch(name, detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail: { ...detail, at: Date.now() } }));
  } catch {}
}

function scheduleAggregateFire() {
  clearTimeout(debTimer);
  debTimer = setTimeout(() => {
    dispatch("hits-updated", { source: "sse", burst: burstCount });
    dispatch("request-hits-refresh", { source: "sse" });
    burstCount = 0;
  }, 250);
}

function clearAllTimers() {
  if (reopenTimer) { clearTimeout(reopenTimer); reopenTimer = null; }
  if (debTimer) { clearTimeout(debTimer); debTimer = null; }
  if (idleTicker) { clearInterval(idleTicker); idleTicker = null; }
}

function bindDisconnectHandler() {
  if (disconnectHandlerBound) return;
  disconnectHandlerBound = true;
  window.addEventListener("sse:disconnect", stopSSE);
}

function unbindDisconnectHandler() {
  if (!disconnectHandlerBound) return;
  disconnectHandlerBound = false;
  window.removeEventListener("sse:disconnect", stopSSE);
}

function readStoredLastId() {
  const raw = (typeof localStorage !== "undefined") ? localStorage.getItem(SSE_LAST_ID_KEY) : null;
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function writeStoredLastId(id) {
  try {
    if (Number.isFinite(id) && id > 0) localStorage.setItem(SSE_LAST_ID_KEY, String(id));
  } catch {}
}

async function seedFromHeadIfNeeded() {
  if (readStoredLastId() > 0) return; // already seeded
  try {
    const res = await fetch(`${API}/events/head`, {
      method: "GET",
      credentials: "include",
      mode: "cors",
      headers: { Accept: "application/json" },
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json && Number.isFinite(json.last_id)) {
      writeStoredLastId(Number(json.last_id));
    }
  } catch {
    // If it fails, we’ll still connect; worst case we replay some history once.
  }
}

function buildEventsURL() {
  const lastId = readStoredLastId();
  const qs = new URLSearchParams();
  if (lastId > 0) qs.set("lastEventId", String(lastId));
  return `${API}/events${qs.toString() ? `?${qs.toString()}` : ""}`;
}

function bumpLastIdFrom(ev, parsed) {
  let id = null;

  // Spec: MessageEvent.lastEventId (string)
  if (ev && typeof ev.lastEventId === "string") {
    const n = Number(ev.lastEventId);
    if (Number.isFinite(n)) id = n;
  }

  // Fallback if your server also includes { id } in data (optional)
  if (id == null && parsed && Number.isFinite(parsed.id)) {
    id = parsed.id;
  }

  if (id != null) {
    const cur = readStoredLastId();
    if (id > cur) writeStoredLastId(id);
  }
}

function openStream() {
  clearTimeout(reopenTimer);
  if (es) { try { es.close(); } catch {} es = null; }

  const url = buildEventsURL();
  es = new EventSource(url, { withCredentials: true });

  es.onopen = () => {
    connected = true;
    backoffIdx = 0;
    lastActivityAt = Date.now();
    dispatch("sse:open");
  };

  es.onerror = () => {
    connected = false;
    dispatch("sse:error");
    if (es && es.readyState === EventSource.CLOSED) {
      try { es.close(); } catch {}
      es = null;
      const delay = BACKOFFS[Math.min(backoffIdx++, BACKOFFS.length - 1)];
      reopenTimer = setTimeout(openStream, delay);
    }
  };

  es.onmessage = (ev) => {
    lastActivityAt = Date.now();
    let obj = null;
    try { obj = JSON.parse(ev.data || "{}"); } catch {}
    bumpLastIdFrom(ev, obj);

    // Optional “aggregate message” pattern:
    if (obj && obj.type === "alert:hit" && obj.payload) {
      dispatch("alert:hit", obj.payload);
      burstCount++;
      scheduleAggregateFire();
    } else if (obj && obj.type === "hit:new") {
      burstCount++;
      scheduleAggregateFire();
    }
  };

  const on = (name, fn) =>
    es.addEventListener(name, (ev) => {
      lastActivityAt = Date.now();
      fn(ev);
    });

  on("ping", (ev) => bumpLastIdFrom(ev, null));

  // New: react to hits:changed
  on("hits:changed", (ev) => {
    bumpLastIdFrom(ev, null);
    burstCount++;
    scheduleAggregateFire();
  });

  on("hit:new", (ev) => {
    bumpLastIdFrom(ev, null);
    burstCount++;
    scheduleAggregateFire();
  });

  on("alert:hit", (ev) => {
    let payload = null;
    try { payload = JSON.parse(ev.data || "null"); } catch {}
    bumpLastIdFrom(ev, payload);
    if (payload && typeof payload === "object") {
      dispatch("alert:hit", payload);
    }
    burstCount++;
    scheduleAggregateFire();
  });

  on("alertlist:updated", (ev) => { bumpLastIdFrom(ev, null); dispatch("alertlist-updated", { source: "sse" }); });
  on("alerttypes:updated", (ev) => { bumpLastIdFrom(ev, null); dispatch("alerttypes-updated", { source: "sse" }); });
}

function startIdleGuard() {
  if (idleTicker) return;
  idleTicker = setInterval(() => {
    if (!es) return;
    const idleMs = Date.now() - (lastActivityAt || 0);
    const isOpen = es && es.readyState === EventSource.OPEN;
    if (idleMs > 70_000 || !isOpen) {
      try { es.close(); } catch {}
      es = null;
      const delay = BACKOFFS[Math.min(backoffIdx++, BACKOFFS.length - 1)];
      reopenTimer = setTimeout(openStream, delay);
    }
  }, 15_000);
}

export function startSSE() {
  if (started) return stopSSE;
  started = true;

  bindDisconnectHandler();

  // seed first (to avoid replay), then open the stream
  (async () => {
    await seedFromHeadIfNeeded();
    openStream();
    startIdleGuard();
  })();

  return stopSSE;
}

export function stopSSE() {
  clearAllTimers();
  if (es) { try { es.close(); } catch {} es = null; }
  if (connected) dispatch("sse:close");
  connected = false;
  started = false;
  unbindDisconnectHandler();
  backoffIdx = 0;
}

export function isSSEConnected() { return connected; }
